/**
 * Back to the host-session issuer after the login page.
 *
 * ComDesk signs staff in through /api/oidc/authorize (backend routes/oidc.ts).
 * Without a HealthPass session cookie the issuer sends the browser to
 * /login?returnTo=<that request>, and once there is a session the browser must
 * go back there by a full page load: it is a backend URL, not a route of the
 * app, so the router's navigate() would only draw the dashboard.
 */
import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { issuerReturn } from "@/utils/issuerReturn";
import { endSession, refreshSession } from "@/utils/sessionRefresh";

/**
 * /login for somebody already signed in. Ordinarily the dashboard. Sent here
 * by the issuer, their session has no cookie yet — it was opened before the
 * cookie existed — so one refresh sets it, and the issuer is asked again.
 */
export function SignedInLogin() {
  const back = issuerReturn(
    new URLSearchParams(useLocation().search).get("returnTo"),
  );

  useEffect(() => {
    if (!back) return;
    void refreshSession().then((token) => {
      // No token: the session is over after all, and going back would only
      // land here again. Signing out ends at the login page.
      if (token) window.location.assign(back);
      else void endSession();
    });
  }, [back]);

  return back ? null : <Navigate to="/" replace />;
}
